import { useEffect, useRef, useState } from "react"
import constate from "constate"
import { useResources } from '../../../utility/use-resources'

const useVisibleMenuItems = () => {
  const [r] = useResources("new-menu", "canaryMenu")
  const menuItems = r()
  const [visibleMenuItems, setVisibleMenuItems] = useState(menuItems)

  const [loaded, setLoaded] = useState(false)
  const containerRef = useRef(null)
  const itemWidths = useRef({})

  const calculateVisibleItems = () => {
    if (!containerRef.current || (Object.keys(itemWidths.current).length !== menuItems.length)) return
    const headerNav = document.getElementById('header-nav')
    if (!headerNav) return

    // Calculate total width of all items 
    let totalWidth = Object.values(itemWidths.current).reduce((acc, item) => acc + item, 0)
    if (totalWidth === 0) {
      // items arent loaded yet
      return
    }
    
    
    const otherNavChildren = Array.from(headerNav.children).filter(child => child !== containerRef.current)
    const otherNavChildrenWidth = otherNavChildren.reduce((acc, child) => {
      const childWidth = child.getBoundingClientRect().width
      return acc + childWidth
    }, 0)

    const availableWidth = (headerNav.getBoundingClientRect().width - otherNavChildrenWidth - 60) * 0.95

    // If all items fit, show them all
    if (totalWidth <= availableWidth) {
      setVisibleMenuItems(menuItems)
      return
    }

    // Get hideable items from the end
    const allItems = [...menuItems]
    let hideableItems = allItems.filter(item => item.hideable)
    let currentItems = [...menuItems]

    // Remove hideable items from the end until everything fits
    while(hideableItems.length > 0 && totalWidth > availableWidth) {
      const hideableItem = hideableItems.pop()

      const itemIndex = currentItems.findIndex(item => item.name === hideableItem.name)
      if (itemIndex !== -1) {
        currentItems.splice(itemIndex, 1)
        totalWidth -= itemWidths.current[hideableItem.name] || 0
      }
    }
    // If no items left or no more hideable items but still not enough space, hide the menu
    if (currentItems.length === 0 ||
      (totalWidth > availableWidth && !currentItems.some(item => item.hideable))) {
      setVisibleMenuItems([])
    } else {
      setVisibleMenuItems(currentItems)
    }
  }

  const loadedHandler = () => {
    setLoaded(true)
    calculateVisibleItems()
  }

  // Calculate which menu items should be visible based on available space
  useEffect(() => {
    window.addEventListener('load', loadedHandler)
    window.addEventListener('resize', calculateVisibleItems)
    return () => {
      window.removeEventListener('load', loadedHandler)
      window.removeEventListener('resize', calculateVisibleItems)
    }
  }, [])

  // Save ref for each menu item
  const setItemRef = (name) => (el) => {
    if (!el || !!itemWidths.current[name]) {
      return
    }
    itemWidths.current[name] = el.getBoundingClientRect().width
    calculateVisibleItems()
  }

  return {
    loaded,
    visibleMenuItems,
    containerRef,
    setItemRef,
  }
}

export const [VisibleMenuItemsProvider, useVisibleMenuItemsContext] = constate(useVisibleMenuItems)